import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { StepIndicator, type SingleStepIndicator } from '.';

export type FormattedStep = {
  text: string;
  number: number;
  isActive: boolean;
  isPassed: boolean;
  expand: boolean;
  expansions?: Omit<
    FormattedStep,
    'expand' | 'expansions' | 'number' | 'final'
  >[];
  final: boolean;
};

export interface StepIndicatorHook {
  formattedSteps: FormattedStep[];
}

export const isPassed = (
  pathname: string,
  url: string | string[],
  allUrls: string[],
): boolean => {
  const pathIndex = allUrls
    .map((current) =>
      Boolean(
        matchPath(pathname, {
          path: current,
          exact: true,
          strict: false,
        }),
      ),
    )
    .indexOf(true);
  if (typeof url === 'string') {
    const urlIndex = allUrls
      .map((current) =>
        Boolean(
          matchPath(url, {
            path: current,
            exact: true,
            strict: false,
          }),
        ),
      )
      .indexOf(true);
    return pathIndex > urlIndex;
  }
  const urlIndexes = url.map(
    (currentURL) =>
      allUrls
        .map((current) =>
          Boolean(
            matchPath(currentURL, {
              path: current,
              exact: true,
              strict: false,
            }),
          ),
        )
        .indexOf(true) < pathIndex,
  );
  return urlIndexes.every((val) => val === true);
};

export const isActive = (
  path: string,
  itemPath: string | string[],
): boolean => {
  if (typeof itemPath === 'string')
    return Boolean(
      matchPath(path, {
        path: itemPath,
        exact: true,
        strict: false,
      }),
    );
  return itemPath.some((ip) =>
    Boolean(
      matchPath(path, {
        path: ip,
        exact: true,
        strict: false,
      }),
    ),
  );
};

const isSingleStep = (step: StepIndicator): step is SingleStepIndicator =>
  (step as SingleStepIndicator).url !== undefined;

const isSingleString = (url: string | string[]): url is string =>
  typeof url === 'string';

export default function useStepIndicator(
  steps: StepIndicator[],
): StepIndicatorHook {
  const { pathname } = useLocation();

  const extractedUrls = useMemo(
    () =>
      steps.reduce((final: string[], step: StepIndicator) => {
        if (isSingleStep(step)) {
          const stepUrl = step.url;
          if (isSingleString(stepUrl)) return [...final, stepUrl];
          return [...final, ...stepUrl];
        }
        const urls = step.substeps.map((s) => s.url).flat();
        return [...final, ...urls];
      }, []),
    [steps],
  );

  const formattedSteps: FormattedStep[] = useMemo(
    () =>
      steps.map((step: StepIndicator, index: number): FormattedStep => {
        const idx = index + 1;
        if (isSingleStep(step)) {
          const active = isActive(pathname, step.url);
          const passed = isPassed(pathname, step.url, extractedUrls);
          return {
            text: step.name,
            number: idx,
            isActive: active,
            isPassed: passed,
            expand: false,
            final: idx === steps.length,
          };
        }
        const active = step.substeps
          .map((substep) => Boolean(isActive(pathname, substep.url)))
          .some((val) => val);
        const passed = step.substeps
          .map((substep) => isPassed(pathname, substep.url, extractedUrls))
          .every((val) => val);
        const expansions = step.substeps.map((substep) => {
          const subActive = Boolean(isActive(pathname, substep.url));
          const subPassed = isPassed(pathname, substep.url, extractedUrls);
          return {
            text: substep.name,
            isActive: subActive,
            isPassed: subPassed,
          };
        });
        return {
          text: step.name,
          number: idx,
          isActive: active,
          isPassed: passed,
          expand: active,
          expansions,
          final: index === steps.length,
        };
      }),
    [steps, extractedUrls, pathname],
  );

  return {
    formattedSteps,
  };
}
